import { AccountBalance, Delete, Edit, Search, TrendingDown, TrendingUp, WorkspacePremium } from "@mui/icons-material";
import {
    Box, Breadcrumbs,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TextField,
    Tooltip,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid } from '../../helpers/formatters';

function TransactionsPage() {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [reconciliationData, setReconciliationData] = useState({
        totalIncome: 0,
        totalOutcome: 0,
        balance: 0,
        transactionCount: 0,
        incomeCount: 0,
        outcomeCount: 0,
        averageTransaction: 0
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const calculateReconciliation = (transactions) => {
        const incomeTransactions = transactions.filter(t => t.type === 'income');
        const outcomeTransactions = transactions.filter(t => t.type === 'outcome');

        const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.net_amount, 0);
        const totalOutcome = outcomeTransactions.reduce((sum, t) => sum + t.net_amount, 0);
        const balance = totalIncome - totalOutcome;
        const transactionCount = transactions.length;
        const averageTransaction = transactionCount > 0 ? (totalIncome + totalOutcome) / transactionCount : 0;

        return {
            totalIncome,
            totalOutcome,
            balance,
            transactionCount,
            averageTransaction
        };
    };

    const fetchTransactions = useCallback(() => {
        setIsLoading(true);

        // Buscar transações paginadas e dados de conciliação em paralelo
        Promise.all([
            api.get(`transactions?page=${currentPage}&limit=${itemsPerPage}&q=${searchTerm}`),
            api.get('transactions/reconciliation')
        ])
            .then(([transactionsResponse, reconciliationResponse]) => {
                console.log('Resposta da API:', transactionsResponse.data);
                const transactions = transactionsResponse.data.data.transactions || [];
                setTransactions(transactions);
                setTotalPages(transactionsResponse.data.data.total_pages || 1);

                // Usar dados de conciliação do backend
                if (reconciliationResponse.data.success) {
                    console.log('Dados de conciliação do backend:', reconciliationResponse.data.data);
                    // Mapear dados do backend para o formato do frontend
                    const backendData = reconciliationResponse.data.data;
                    setReconciliationData({
                        totalIncome: backendData.total_income || 0,
                        totalOutcome: backendData.total_outcome || 0,
                        balance: backendData.balance || 0,
                        transactionCount: backendData.transaction_count || 0,
                        incomeCount: backendData.income_count || 0,
                        outcomeCount: backendData.outcome_count || 0,
                        averageTransaction: backendData.average_transaction || 0
                    });
                } else {
                    // Fallback para cálculo local se o backend falhar
                    const reconciliation = calculateReconciliation(transactions);
                    setReconciliationData(reconciliation);
                }
            })
            .catch(error => {
                console.error('Erro ao buscar transações:', error);
                console.error('Detalhes do erro:', error.response?.data);
                setTransactions([]); // Garante que transactions seja sempre um array
                setTotalPages(1);
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, searchTerm]);

    useListFetch(fetchTransactions, [currentPage, itemsPerPage, appliedSearchTerm, fetchTransactions]);

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(true);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const getTransactionTypeLabel = (type) => {
        switch (type) {
            case 'income':
                return 'Entrada';
            case 'outcome':
                return 'Saída';
            default:
                return type;
        }
    };

    const getOutcomeCategoryLabel = (category) => {
        switch (category) {
            case 'withdrawal':
                return 'Retirada';
            case 'expense':
                return 'Despesa';
            default:
                return '';
        }
    };

    const getPaymentMethodLabel = (method) => {
        switch (method) {
            case 'credit_card':
                return 'Cartão de Crédito';
            case 'debit_card':
                return 'Cartão de Débito';
            case 'pix':
                return 'PIX';
            case 'bank_transfer':
                return 'Transferência Bancária';
            case 'cash':
                return 'Dinheiro';
            case 'other':
                return 'Outro';
            default:
                return method;
        }
    };

    const getTransactionTypeColor = (type) => {
        switch (type) {
            case 'income':
                return 'success.main';
            case 'outcome':
                return 'error.main';
            default:
                return 'text.primary';
        }
    };

    const handleEditTransaction = (transactionId) => {
        navigate(`/admin/edit-transaction/${transactionId}`);
    };

    const handleDeleteTransaction = (transactionId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta transação será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`transactions/${transactionId}`)
                        .then(response => {
                            setLoadingDelete(false);
                            // Atualiza o estado após remoção
                            setTransactions(transactions.filter(transaction => transaction.id !== transactionId));
                            Swal.fire('Removido!', 'A transação foi removida com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover a transação:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover a transação.', 'error');
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Transações</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4">
                        Transações
                    </Typography>
                    <WorkspacePremium color="warning" />
                </Box>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar transação..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => navigate('/admin/create-transaction')}
                        fullWidth={isMobile}
                        sx={{
                            backgroundColor: '#4caf50',
                            '&:hover': {
                                backgroundColor: '#45a049'
                            }
                        }}
                    >
                        + Nova Transação
                    </Button>
                </Box>
            </Box>

            {/* Dashboard de Conciliação */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Card de Entradas */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUp color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6" color="success.main">
                                    Entradas
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                                {formatCurrencyInCents(reconciliationData.totalIncome)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.incomeCount} transação(ões)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Card de Saídas */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingDown color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6" color="error.main">
                                    Saídas
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                                {formatCurrencyInCents(reconciliationData.totalOutcome)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.outcomeCount} transação(ões)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Card de Saldo */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AccountBalance
                                    color={reconciliationData.balance >= 0 ? "success" : "error"}
                                    sx={{ mr: 1 }}
                                />
                                <Typography
                                    variant="h6"
                                    color={reconciliationData.balance >= 0 ? "success.main" : "error.main"}
                                >
                                    Saldo
                                </Typography>
                            </Box>
                            <Typography
                                variant="h4"
                                color={reconciliationData.balance >= 0 ? "success.main" : "error.main"}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {formatCurrencyInCents(reconciliationData.balance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.balance >= 0 ? 'Positivo' : 'Negativo'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Card de Resumo */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" color="primary.main">
                                    Resumo
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {reconciliationData.transactionCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total de transações
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Média: {formatCurrencyInCents(reconciliationData.averageTransaction)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Desconto/Taxas</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor Líquido</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Método de Pagamento</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Autorização</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Origem</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.length > 0 ? transactions.map((transaction) => (
                            <TableRow key={transaction.id}>
                                <TableCell>{formatDate(transaction.created_at)}</TableCell>
                                <TableCell>
                                    <Typography
                                        color={getTransactionTypeColor(transaction.type)}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {getTransactionTypeLabel(transaction.type)}
                                        {transaction.type === 'outcome' && getOutcomeCategoryLabel(transaction.outcome_category)
                                            ? ` · ${getOutcomeCategoryLabel(transaction.outcome_category)}`
                                            : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell>{formatCurrencyInCents(transaction.amount)}</TableCell>
                                <TableCell>{formatCurrencyInCents(transaction.deduction)}</TableCell>
                                <TableCell>
                                    <Typography
                                        color={getTransactionTypeColor(transaction.type)}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {formatCurrencyInCents(transaction.net_amount)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{getPaymentMethodLabel(transaction.payment_method)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {transaction.authorization_id || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>{transaction.source || '—'}</TableCell>
                                <TableCell>
                                    {transaction.order_id ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {transaction.description && (
                                                <Typography variant="body2">{transaction.description}</Typography>
                                            )}
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => {
                                                    const orderId = formatUuid(transaction.order_id);
                                                    console.log('Navegando para pedido:', orderId);
                                                    navigate(`/admin/orders?search=${orderId}`);
                                                }}
                                                sx={{
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                referente ao pedido {formatUuid(transaction.order_id)}
                                            </Link>
                                        </Box>
                                    ) : (
                                        transaction.description || '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Editar">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditTransaction(transaction.id)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteTransaction(transaction.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography variant="body1" color="text.secondary">
                                        Nenhuma transação encontrada
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Total: {transactions.length} transação(ões)
                </Typography>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default TransactionsPage;
